import { Link, useParams, Navigate } from "react-router-dom";
import { Clock } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import CTASection from "../components/blocks/CTASection";
import { INSIGHTS } from "../data/insights";

export default function InsightArticlePage() {
  const { slug } = useParams();
  const article = INSIGHTS.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/knowledge" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: "Rakesh Gopinathan",
      jobTitle: "Founder & CEO, The Vantage Group™",
      sameAs: "https://www.linkedin.com/in/rakeshgopinathan/",
    },
    publisher: { "@type": "Organization", name: "The Vantage Institute™" },
    inLanguage: "en",
  };

  return (
    <>
      <SEO
        title={`${article.title} | The Vantage Institute™`}
        description={article.excerpt}
        path={`/knowledge/insights/${article.slug}`}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Knowledge Hub", to: "/knowledge" }, { label: article.title }]} />

      <article className="container-x py-14 sm:py-20" data-testid="insight-article">
        <header className="max-w-3xl">
          <p className="eyebrow">Executive Insight</p>
          <h1 className="mt-4 font-display text-3xl font-semibold leading-tight text-navy sm:text-4xl lg:text-5xl">
            {article.title}
          </h1>
          <p className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 font-sub text-xs font-semibold uppercase tracking-[0.14em] text-slate">
            <Clock size={14} aria-hidden="true" className="text-gold-deep" />
            {article.minutes} min read • {article.date} • By{" "}
            <Link to="/founder/rakesh-gopinathan" className="text-navy underline decoration-gold underline-offset-4 transition-colors hover:text-gold-deep" data-testid="insight-author-link">
              Rakesh Gopinathan
            </Link>
          </p>
          <span className="gold-rule mt-6" aria-hidden="true" />
          <p className="mt-6 text-base leading-relaxed text-slate md:text-lg">{article.excerpt}</p>
        </header>

        <div className="mt-10 max-w-3xl">
          {article.sections.map((s) => (
            <section key={s.heading} className="border-t border-platinum py-8" data-testid={`insight-section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
              <h2 className="font-sub text-lg font-semibold text-navy">{s.heading}</h2>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-slate">{p}</p>
              ))}
            </section>
          ))}
          <div className="mt-6 border-l-2 border-gold bg-paper p-5 sm:p-6">
            <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">Go further</p>
            <Link to={article.related.to} className="text-link mt-3 inline-flex min-h-[44px] items-center" data-testid="insight-related-link">
              {article.related.label}
            </Link>
          </div>
        </div>
      </article>

      <CTASection
        title="Turn insight into capability."
        text="Insights change what you think. Vantage programmes change what you can do."
        primaryLabel="Explore Programmes"
        primaryTo="/programmes"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="insight-cta"
      />
    </>
  );
}
